import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from '../../../services/user.service';
import {
  PerfilSeccionEstado,
  PerfilSeccionId,
} from '../../data/perfil-microempresario.content';
import { PERFIL_DOC_PASO, isDeclaracionRentaDoc } from '../documentos/perfil-documentos.util';
import { aplicaDeclaracionRentaPorRegla } from '../documentos/perfil-renta.util';
import { isPerfilJuridica } from '../perfil-tipo.util';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import {
  PERFIL_PROGRESS_BASE_DEFINITIONS,
  PERFIL_PROGRESS_BASE_DEFINITIONS_PJ,
  PERFIL_PROGRESS_DOCS_SUBSECTION_ID,
  PerfilDocumentoDef,
  PerfilProgressFieldDef,
  PerfilProgressSectionDef,
  PerfilProgressSnapshot,
  PerfilProgressSubsectionDef,
  PerfilProgressValues,
  PerfilSectionProgress,
  PerfilSubsectionProgress,
  labelForPasoId,
} from './perfil-progress.definitions';


/** Campos de ingresos que solo aplican a Tradicional (o Express+PAR-128 para flujo). */
const INGRESOS_TRADICIONAL_ONLY_KEYS = new Set([
  'declaraRenta',
]);

const INGRESOS_FLUJO_SUBSECTION_ID = 'flujo-caja';

/** En ingresos, docs del paso `documentos` se muestran junto a activos/soportes. */
const PASO_ALIAS_POR_SECCION: Partial<
  Record<PerfilSeccionId, Record<string, string>>
> = {
  'sobre-ingresos': {
    [PERFIL_DOC_PASO.documentos]: 'activos-soportes',
  },
};

type SectionValuesMap = Partial<Record<PerfilSeccionId, PerfilProgressValues>>;
type SectionInvalidKeysMap = Partial<Record<PerfilSeccionId, Set<string>>>;

/**
 * Calcula % de avance del perfil por sección y subsección.
 *
 * Campos de formulario: catálogo base fijo (PN 3 secs / PJ 2 secs).
 * Documentos: config dinámica desde tiposDocumentos/perfil/{tipo}.
 * Solo los `obligatorio: true` del catálogo suman al denominador del %.
 */
@Injectable({ providedIn: 'root' })
export class PerfilProgressService {
  private values: SectionValuesMap = {};
  private invalidKeys: SectionInvalidKeysMap = {};
  private documentos: PerfilDocumentoDef[] = [];
  private readonly snapshotSubject: BehaviorSubject<PerfilProgressSnapshot>;

  readonly snapshot$: Observable<PerfilProgressSnapshot>;

  constructor(
    private readonly segmentoService: PerfilSegmentoService,
    private readonly userService: UserService
  ) {
    this.snapshotSubject = new BehaviorSubject(this.buildSnapshot());
    this.snapshot$ = this.snapshotSubject.asObservable();
  }

  /** Base PN o PJ según tipoPersona de sesión. */
  getBaseDefinitions(): ReadonlyArray<PerfilProgressSectionDef> {
    return isPerfilJuridica(this.userService.getUser()?.tipoPersona)
      ? PERFIL_PROGRESS_BASE_DEFINITIONS_PJ
      : PERFIL_PROGRESS_BASE_DEFINITIONS;
  }

  /**
   * Definiciones resueltas (base + documentos inyectados por sección/paso).
   */
  getDefinitions(): ReadonlyArray<PerfilProgressSectionDef> {
    return this.getBaseDefinitions().map((base) => this.resolveSectionDef(base));
  }

  /** Documentos vigentes (API vía setDocumentosConfig). */
  getDocumentosConfig(): ReadonlyArray<PerfilDocumentoDef> {
    return this.documentos;
  }

  getDocumentosForSection(
    seccionId: PerfilSeccionId
  ): ReadonlyArray<PerfilDocumentoDef> {
    return this.documentos.filter((d) => d.seccionId === seccionId);
  }

  getDocumentosForPaso(
    seccionId: PerfilSeccionId,
    pasoId: string
  ): ReadonlyArray<PerfilDocumentoDef> {
    return this.getDocumentosForSection(seccionId).filter(
      (d) => this.resolveInjectPasoId(seccionId, d) === pasoId
    );
  }

  /**
   * Reemplaza la config de documentos (entrada del back).
   * Recalcula el snapshot inmediatamente.
   */
  setDocumentosConfig(documentos: ReadonlyArray<PerfilDocumentoDef>): void {
    this.documentos = documentos.map((d) => ({ ...d }));
    this.refresh();
  }

  replaceDocumentosDeSeccion(
    seccionId: PerfilSeccionId,
    docs: ReadonlyArray<PerfilDocumentoDef>
  ): void {
    const rest = this.documentos.filter((d) => d.seccionId !== seccionId);
    this.setDocumentosConfig([...rest, ...docs]);
  }

  getSnapshot(): PerfilProgressSnapshot {
    return this.snapshotSubject.value;
  }

  /**
   * Conteo persistible en la solicitud (misma base que el hub v2).
   * Incluye campos y documentos obligatorios de las 3 secciones.
   */
  getCamposConteo(): {
    camposLlenados: number;
    camposObligatorios: number;
  } {
    const snapshot = this.getSnapshot();
    const camposObligatorios = snapshot.sections.reduce(
      (acc, s) => acc + s.totalRequired,
      0
    );
    const camposLlenados = snapshot.sections.reduce(
      (acc, s) => acc + s.filledRequired,
      0
    );
    return {
      camposObligatorios,
      camposLlenados: Math.min(camposLlenados, camposObligatorios),
    };
  }

  getSectionProgress(sectionId: PerfilSeccionId): PerfilSectionProgress {
    return (
      this.getSnapshot().sections.find((s) => s.id === sectionId) ??
      this.computeSection(sectionId, this.values[sectionId] ?? {})
    );
  }

  getSectionValues(sectionId: PerfilSeccionId): PerfilProgressValues {
    return { ...(this.values[sectionId] ?? {}) };
  }

  updateSectionValues(
    sectionId: PerfilSeccionId,
    values: PerfilProgressValues,
    invalidKeys?: ReadonlyArray<string>
  ): PerfilSectionProgress {
    this.values[sectionId] = {
      ...(this.values[sectionId] ?? {}),
      ...values,
    };
    if (invalidKeys) {
      this.invalidKeys[sectionId] = new Set(invalidKeys);
    }
    const snapshot = this.buildSnapshot();
    this.snapshotSubject.next(snapshot);
    return snapshot.sections.find((s) => s.id === sectionId)!;
  }

  /** Reemplaza el snapshot de avance en memoria (cálculo en vivo de la sección). */
  replaceAllSectionValues(
    next: Partial<Record<PerfilSeccionId, PerfilProgressValues>>
  ): PerfilProgressSnapshot {
    this.values['sobre-ti'] = { ...(next['sobre-ti'] ?? {}) };
    this.values['sobre-negocio'] = { ...(next['sobre-negocio'] ?? {}) };
    this.values['sobre-ingresos'] = { ...(next['sobre-ingresos'] ?? {}) };
    return this.refresh();
  }

  refresh(): PerfilProgressSnapshot {
    const snapshot = this.buildSnapshot();
    this.snapshotSubject.next(snapshot);
    return snapshot;
  }

  computeSection(
    sectionId: PerfilSeccionId,
    values: PerfilProgressValues
  ): PerfilSectionProgress {
    const base = this.getBaseDefinitions().find((s) => s.id === sectionId);
    if (!base) {
      return this.emptySection(sectionId);
    }

    const resolved = this.resolveSectionDef(base);
    const docs = this.getDocumentosForSection(sectionId);

    const subsections: PerfilSubsectionProgress[] = resolved.subsections
      .filter((sub) => this.isSubsectionActive(sectionId, sub.id))
      .map((sub) => this.computeSubsection(sub, values, sectionId));

    const filledRequired = subsections.reduce(
      (acc, s) => acc + s.filledRequired,
      0
    );
    const totalRequired = subsections.reduce(
      (acc, s) => acc + s.totalRequired,
      0
    );
    // Cada subpaso/formulario pesa igual (no por cantidad de campos).
    const weighed = subsections.filter((s) => s.totalRequired > 0);
    const percent = weighed.length
      ? Math.round(
          weighed.reduce((acc, s) => acc + s.percent, 0) / weighed.length
        )
      : this.toPercent(filledRequired, totalRequired);

    const defaultCheckpoints =
      base.checkpoints ?? Math.max(1, resolved.subsections.length);
    const juridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    const checkpoints =
      sectionId === 'sobre-ingresos'
        ? juridica
          ? defaultCheckpoints
          : this.segmentoService.muestraFlujoCaja
            ? 3
            : 2
        : defaultCheckpoints;

    return {
      id: sectionId,
      filledRequired,
      totalRequired,
      percent,
      checkpoints,
      estado: this.resolveEstado(percent, filledRequired, totalRequired),
      subsections,
      documentos: docs,
    };
  }

  isFilled(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (typeof value === 'boolean') {
      return value === true;
    }
    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }

  /**
   * Une subsecciones base + campos de documentos por `pasoId`.
   * Sin keys de documentos quemadas en el base.
   */
  private resolveSectionDef(
    base: PerfilProgressSectionDef
  ): PerfilProgressSectionDef {
    const docs = this.getDocumentosForSection(base.id);
    if (!docs.length) {
      return base;
    }

    const byPaso = new Map<string, PerfilDocumentoDef[]>();
    for (const doc of docs) {
      const paso = this.resolveInjectPasoId(base.id, doc);
      const list = byPaso.get(paso) ?? [];
      list.push(doc);
      byPaso.set(paso, list);
    }

    const subsections: PerfilProgressSubsectionDef[] = base.subsections.map(
      (sub) => {
        const pasoDocs = byPaso.get(sub.id) ?? [];
        byPaso.delete(sub.id);
        if (!pasoDocs.length) {
          return sub;
        }
        return {
          ...sub,
          fields: [
            ...sub.fields,
            ...pasoDocs.map((d) => ({
              key: d.valueKey,
              required: d.obligatorio,
            })),
          ],
        };
      }
    );

    // Pasos con docs que no existen en el base (p.ej. `documentos` en sobre-ti).
    for (const [pasoId, pasoDocs] of byPaso) {
      if (!pasoDocs.length) {
        continue;
      }
      const insertAt = this.insertIndexForPaso(base.id, pasoId, subsections);
      const block: PerfilProgressSubsectionDef = {
        id: pasoId,
        label: labelForPasoId(pasoId),
        fields: pasoDocs.map((d) => ({
          key: d.valueKey,
          required: d.obligatorio,
        })),
      };
      subsections.splice(insertAt, 0, block);
    }

    return { ...base, subsections };
  }

  private resolveInjectPasoId(
    seccionId: PerfilSeccionId,
    doc: PerfilDocumentoDef
  ): string {
    const raw = doc.pasoId || PERFIL_PROGRESS_DOCS_SUBSECTION_ID;
    const alias = PASO_ALIAS_POR_SECCION[seccionId]?.[raw];
    const juridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    if (
      alias &&
      !(seccionId === 'sobre-ingresos' && juridica)
    ) {
      return alias;
    }
    if (
      seccionId === 'sobre-ti' &&
      raw === PERFIL_DOC_PASO.documentos &&
      juridica
    ) {
      return PERFIL_DOC_PASO.fotos;
    }
    return raw;
  }

  /** Dónde insertar un paso nuevo (p.ej. documentos tras datos personales). */
  private insertIndexForPaso(
    seccionId: PerfilSeccionId,
    pasoId: string,
    subsections: ReadonlyArray<PerfilProgressSubsectionDef>
  ): number {
    if (pasoId === PERFIL_DOC_PASO.fotos) {
      const ubicacion = subsections.findIndex((s) => s.id === 'ubicacion');
      if (ubicacion >= 0) {
        return ubicacion + 1;
      }
      const datosEmpresa = subsections.findIndex((s) => s.id === 'datos-empresa');
      if (datosEmpresa >= 0) {
        return datosEmpresa + 1;
      }
      return subsections.length;
    }
    if (pasoId === PERFIL_DOC_PASO.infoAdicional) {
      return subsections.length;
    }
    if (pasoId === PERFIL_PROGRESS_DOCS_SUBSECTION_ID) {
      if (seccionId === 'sobre-ti') {
        const datosEmpresa = subsections.findIndex(
          (s) => s.id === 'datos-empresa'
        );
        if (datosEmpresa >= 0) {
          return datosEmpresa + 1;
        }
        return subsections.length;
      }
      if (seccionId === 'sobre-negocio') {
        const ubicacion = subsections.findIndex((s) => s.id === 'ubicacion');
        return ubicacion >= 0 ? ubicacion + 1 : subsections.length;
      }
      if (seccionId === 'sobre-ingresos') {
        const activos = subsections.findIndex((s) => s.id === 'activos-soportes');
        return activos >= 0 ? activos + 1 : subsections.length;
      }
    }
    return subsections.length;
  }

  private computeSubsection(
    sub: PerfilProgressSubsectionDef,
    values: PerfilProgressValues,
    sectionId?: PerfilSeccionId
  ): PerfilSubsectionProgress {
    const requiredFields = sub.fields.filter((f) =>
      this.isFieldRequired(f, values, sectionId)
    );
    const totalRequired = requiredFields.length;
    const invalid = this.invalidKeys[sectionId ?? 'sobre-ti'] ?? new Set();
    const filledRequired = requiredFields.filter(
      (f) => this.isFilled(values[f.key]) && !invalid.has(f.key)
    ).length;

    return {
      id: sub.id,
      label: sub.label,
      filledRequired,
      totalRequired,
      percent: this.toPercent(filledRequired, totalRequired),
    };
  }

  private isFieldRequired(
    field: PerfilProgressFieldDef,
    values: PerfilProgressValues,
    sectionId?: PerfilSeccionId
  ): boolean {
    // Documento del catálogo: solo `obligatorio` de BD.
    const docDef = this.documentos.find((d) => d.valueKey === field.key);
    if (docDef) {
      if (isDeclaracionRentaDoc(docDef)) {
        if (isPerfilJuridica(this.userService.getUser()?.tipoPersona)) {
          return !!docDef.obligatorio;
        }
        if (this.segmentoService.esExpress) {
          return false;
        }
        if (this.resolveAplicaDeclaracionRenta(values)) {
          return true;
        }
        const v = values['declaraRenta'];
        return v === 'si' || String(v).toLowerCase() === 'si';
      }
      return !!docDef.obligatorio;
    }
    // Keys doc_* sin entrada en catálogo → no obligatorias.
    if (field.key.startsWith('doc_')) {
      return false;
    }
    if (field.required === false) {
      return false;
    }
    if (
      sectionId === 'sobre-ingresos' &&
      this.segmentoService.esExpress &&
      INGRESOS_TRADICIONAL_ONLY_KEYS.has(field.key)
    ) {
      return false;
    }
    // Si la regla automática exige renta, no pedimos la pregunta Si/No.
    if (
      field.key === 'declaraRenta' &&
      this.resolveAplicaDeclaracionRenta(values)
    ) {
      return false;
    }
    if (field.requiredWhen) {
      const actual = values[field.requiredWhen.key];
      const expected = field.requiredWhen.equals;
      return actual == expected || String(actual) === String(expected);
    }
    return true;
  }

  /** PAR-096 + fecha (null = no aplica) o PAR-117 + valorVentas. */
  private resolveAplicaDeclaracionRenta(
    values: PerfilProgressValues
  ): boolean {
    if (values['aplicaDeclaracionRenta'] === true) {
      return true;
    }
    const p = this.segmentoService.paramsSnapshot;
    return aplicaDeclaracionRentaPorRegla({
      fechaRegistroActividadEconomica: (values[
        'fechaRegistroActividadEconomica'
      ] ?? null) as string | Date | null,
      valorVentas: (values['valorVentas'] ?? null) as
        | number
        | string
        | null,
      tiempoMinimoMeses: p.tiempoMinimoDeclaracionRenta,
      valorLimiteVentas: p.valorLimiteVentas,
    });
  }

  private isSubsectionActive(
    sectionId: PerfilSeccionId,
    subsectionId: string
  ): boolean {
    if (
      sectionId === 'sobre-ingresos' &&
      subsectionId === INGRESOS_FLUJO_SUBSECTION_ID
    ) {
      return this.segmentoService.muestraFlujoCaja;
    }
    // Omitir subpasos vacíos (p.ej. fotos sin docs en catálogo).
    if (subsectionId === PERFIL_DOC_PASO.fotos) {
      const docs = this.getDocumentosForPaso(sectionId, subsectionId);
      return docs.length > 0;
    }
    return true;
  }

  private buildSnapshot(): PerfilProgressSnapshot {
    const sections = this.getBaseDefinitions().map((def) =>
      this.computeSection(def.id, this.values[def.id] ?? {})
    );
    const globalPercent = sections.length
      ? Math.round(
          sections.reduce((acc, s) => acc + s.percent, 0) / sections.length
        )
      : 0;
    const seccionesCompletas = sections.filter(
      (s) => s.estado === 'completo'
    ).length;

    return { sections, globalPercent, seccionesCompletas };
  }

  private resolveEstado(
    percent: number,
    filledRequired: number,
    totalRequired: number
  ): PerfilSeccionEstado {
    // Sin obligatorios (solo opcionales / sección vacía): no marcar completo.
    if (totalRequired <= 0) {
      return filledRequired > 0 ? 'en_proceso' : 'pendiente';
    }
    if (percent >= 100) {
      return 'completo';
    }
    if (filledRequired > 0 || percent > 0) {
      return 'en_proceso';
    }
    return 'pendiente';
  }

  private toPercent(filled: number, total: number): number {
    if (total <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, Math.round((filled / total) * 100)));
  }

  private emptySection(sectionId: PerfilSeccionId): PerfilSectionProgress {
    return {
      id: sectionId,
      filledRequired: 0,
      totalRequired: 0,
      percent: 0,
      checkpoints: 1,
      estado: 'pendiente',
      subsections: [],
      documentos: [],
    };
  }

}
